import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { AuthModel } from 'pocketbase'
import * as Sentry from '@sentry/react'
import type { ProfileRole } from '../types'
import { getCurrentUser, onAuthChange, pb, safeAuthRefresh, fetchFreshUser } from '../lib/pocketbase'
import { showToast } from '../components/Toast'
import { loadPreferencesFromServer } from '../utils/userPreferences'

type SessionContextValue = {
  user: AuthModel | null
  loading: boolean
  role: ProfileRole | undefined
  refreshUser: () => Promise<boolean>
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthModel | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let unsubscribed = false
    const initialUser = getCurrentUser()
    setUser(initialUser)

    // Auto-refresh user data on app load to ensure we have latest agency_id, role, etc.
    // This handles cases where user data was updated in PocketBase after login
    const refreshOnLoad = async () => {
      if (initialUser && pb.authStore.isValid) {
        try {
          // Timeout prevents infinite hang if PocketHost is unreachable
          const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Auth refresh timeout')), 8000)
          )
          await Promise.race([
            safeAuthRefresh({ minIntervalMs: 60_000 }),
            timeout,
          ])
          if (!unsubscribed) {
            setUser(pb.authStore.model as AuthModel | null)
          }
        } catch (err: any) {
          console.warn('Failed to refresh auth on load:', err)
        }
      }
      if (!unsubscribed) {
        setLoading(false)
        loadPreferencesFromServer()
      }
    }
    refreshOnLoad()

    // Set initial Sentry user context
    if (initialUser) {
      Sentry.setUser({
        id: initialUser.id,
        email: initialUser.email,
        username: initialUser.name || initialUser.first_name,
      })
    }

    const unsubscribe = onAuthChange(nextUser => {
      if (!unsubscribed) {
        setUser(nextUser)
        if (nextUser) {
          Sentry.setUser({
            id: nextUser.id,
            email: nextUser.email,
            username: nextUser.name || (nextUser as any).first_name,
          })
          loadPreferencesFromServer()
        } else {
          Sentry.setUser(null)
        }
      }
    })

    // Refresh user data when window regains focus (in case it was updated in PocketBase admin)
    const handleFocus = async () => {
      if (pb.authStore.isValid && !unsubscribed) {
        try {
          await safeAuthRefresh({ minIntervalMs: 120_000 })
          // The onChange callback will fire automatically and update the user state
        } catch {
          // Silently fail - user might not be logged in anymore or token expired
          // This is expected behavior, so we don't need to log it
        }
      }
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      unsubscribed = true
      unsubscribe()
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const role = useMemo(() => {
    // Extract role from user object (check various possible locations)
    return user?.role || (user as any)?.profile?.role || (user as any)?.expand?.profile?.role || undefined
  }, [user])

  // Function to manually refresh user data
  const refreshUser = async (): Promise<boolean> => {
    if (pb.authStore.isValid) {
      try {
        await fetchFreshUser()
        setUser(pb.authStore.model as AuthModel | null)
        return true
      } catch (err) {
        showToast((err as any).message || 'Failed to refresh session', 'error')
        return false
      }
    }
    return false
  }

  const value = useMemo(() => ({
    user,
    loading,
    role,
    refreshUser,
  }), [user, loading, role])

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  const ctx = useContext(SessionContext)
  if (!ctx) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return ctx
}

/**
 * Hook to manually refresh the current user's session data
 * Useful when you know the user data has been updated in PocketBase admin
 */
export function useRefreshSession() {
  return async () => {
    if (pb.authStore.isValid) {
      try {
        await fetchFreshUser()
        return true
      } catch (err) {
        showToast((err as any).message || 'Failed to refresh session', 'error')
        return false
      }
    }
    return false
  }
}
